import React, { useState } from 'react';
import './SignUp.css';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  sendEmailVerification
} from 'firebase/auth';
import {
  collection,
  addDoc,
  setDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPills } from '@fortawesome/free-solid-svg-icons';

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    tenantName: '',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    otp: ''
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [phoneUID, setPhoneUID] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSendOtp = async () => {
  setError('');
  try {
    // Clear previous instance if exists
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
    }

    // Create new verifier
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {
        // This will trigger after reCAPTCHA succeeds
        console.log('reCAPTCHA solved');
      },
      'expired-callback': () => {
        setError('reCAPTCHA expired. Please try again.');
      }
    });

    // Explicitly render (sometimes needed for invisible)
    await window.recaptchaVerifier.render();

    const formattedPhone = formData.phone.startsWith('+91')
      ? formData.phone
      : '+91' + formData.phone.replace(/^0+/, '');

    const confirmationResult = await signInWithPhoneNumber(
      auth,
      formattedPhone,
      window.recaptchaVerifier
    );

    window.confirmationResult = confirmationResult;
    setOtpSent(true);
    alert('OTP sent successfully!');
  } catch (err) {
    console.error('OTP send error:', err);
    setError(`Failed to send OTP: ${err.message}`);
    // Clear failed instance
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  }
};

  const handleVerifyOtp = async () => {
    try {
      const result = await window.confirmationResult.confirm(formData.otp);
      const phoneUser = result.user;
      setPhoneUID(phoneUser.uid);
      setOtpVerified(true);
      alert('Phone number verified successfully');
    } catch (err) {
      console.error(err);
      setError('Invalid OTP: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match.');
    }

    if (!otpVerified) {
      return setError('Please verify your phone number using OTP.');
    }

    try {
      // 1. Create email/password user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const emailUser = userCredential.user;
      await sendEmailVerification(emailUser);
      alert('Verification email sent.');

      // 2. Create tenant
      const tenantDoc = await addDoc(collection(db, 'tenants'), {
        tenantName: formData.tenantName,
        createdAt: new Date()
      });

      const tenantId = tenantDoc.id;

      // 3. Create user doc with both UIDs
      await setDoc(doc(db, 'users', emailUser.uid), {
        uids: [emailUser.uid, phoneUID],
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: 'Manager',
        tenantId,
        plan: 'trial',
        trialStart: new Date(),
        trialEnd: Timestamp.fromDate(
          new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        ),
        createdAt: new Date()
      });

      // 4. Navigate to login
      navigate('/login', { state: { from: location.state?.from } });
    } catch (err) {
      console.error(err);
      setError('Signup failed: ' + err.message);
    }
  };

  const handleNavClick = (path) => navigate(path);

  return (
    <div className='signup-background'>
      <nav className='unauth-navbar'>
        <div className='unauth-logo' onClick={() => handleNavClick('/')}>
          <FontAwesomeIcon icon={faPills} className='unauth-header-icon' /> ClinicSync
        </div>
      </nav>

      <div className='extra-cont'>
        <div className='signup-container'>
          <h2 className='signup-title'>Create Your Clinic Account</h2>
          <form onSubmit={handleSubmit} className='signup-form'>
            <input
              type='text'
              name='tenantName'
              placeholder='Clinic/Store Name'
              value={formData.tenantName}
              onChange={handleChange}
              required
            />
            <input
              type='text'
              name='name'
              placeholder='Your Name'
              value={formData.name}
              onChange={handleChange}
              required
            />
            <input
              type='email'
              name='email'
              placeholder='Email Address'
              value={formData.email}
              onChange={handleChange}
              required
            />
            <input
              type='number'
              name='phone'
              placeholder='Phone Number'
              value={formData.phone}
              onChange={handleChange}
              required
            />
            <div className='otp-section'>
              <input
                type='number'
                name='otp'
                placeholder='Enter OTP'
                value={formData.otp}
                onChange={handleChange}
                required
              />
              <button
                type='button'
                className='otp-button'
                onClick={handleSendOtp}
                disabled={otpSent}
              >
                {otpSent ? 'OTP Sent' : 'Send OTP'}
              </button>
              {<button
                type='button'
                className='verify-otp-button'
                onClick={handleVerifyOtp}
                disabled={!otpSent || otpVerified}
              >
                {otpVerified ? 'Verified' : 'Verify OTP'}
              </button>}
            </div>
            <input
              type='password'
              name='password'
              placeholder='Password'
              value={formData.password}
              onChange={handleChange}
              required
            />
            <input
              type='password'
              name='confirmPassword'
              placeholder='Confirm Password'
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
            {error && <p className='error'>{error}</p>}
            <button type='submit' className='signup-button'>
              Sign Up
            </button>
            <div className='signup-form-terms'>
              <input type='checkbox' required />
              <p>
                By signing up, you agree to our{' '}
                <Link to='/terms'>Terms & Conditions</Link> and acknowledge our{' '}
                <Link to='/privacy'>Privacy Policy</Link>
              </p>
            </div>
          </form>
          Already a user?{' '}
          <Link to='/login' state={{ from: location.state?.from }} className='rdr-to-login' title='Go to User Login'>
            Login
          </Link>
        </div>
      </div>
      {/* 🔒 Required for invisible reCAPTCHA */}
      <div id='recaptcha-container'></div>
    </div>
  );
};

export default SignUp;
